import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { BrowserRouter, Link, Route, Routes, useParams } from 'react-router-dom';
import { ApiError, KbClient } from './api/client';
import { KbClientContext } from './api/hooks';
import { AuthProvider, useAuth, type KeycloakFactory } from './auth/AuthProvider';
import { Loading } from './components/ErrorPanel';
import { Layout } from './components/Layout';
import type { AppConfig } from './config';
import { AppConfigContext, useAppConfig } from './configContext';
import { ChoosePilot } from './pages/ChoosePilot';
import { Home } from './pages/Home';
import { Item } from './pages/Item';
import { Search } from './pages/Search';
import { SignIn } from './pages/SignIn';
import { Topic } from './pages/Topic';
import { strings } from './strings';

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Access problems don't fix themselves; only retry what might be transient.
        retry: (count, error) => count < 2 && !(error instanceof ApiError && error.kind !== 'network' && error.kind !== 'server'),
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  });
}

function TenantLayout({ children }: { children: ReactNode }) {
  const { tenant = '' } = useParams();
  return <Layout tenantLink={`/t/${encodeURIComponent(tenant)}`}>{children}</Layout>;
}

function NotFound() {
  return (
    <Layout>
      <h1 className="mt-0">{strings.notFoundPage.title}</h1>
      <p>{strings.notFoundPage.body}</p>
      <Link to="/">{strings.notFoundPage.home}</Link>
    </Layout>
  );
}

/** Everything behind sign-in; the KB client uses the signed-in user's token. */
function SignedInApp() {
  const { getToken } = useAuth();
  const { apiUrl } = useAppConfig();
  const client = useMemo(() => new KbClient(apiUrl, getToken), [apiUrl, getToken]);
  const [queryClient] = useState(makeQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <KbClientContext.Provider value={client}>
        <Routes>
          <Route path="/" element={<Layout><ChoosePilot /></Layout>} />
          <Route path="/t/:tenant" element={<TenantLayout><Home /></TenantLayout>} />
          <Route path="/t/:tenant/search" element={<TenantLayout><Search /></TenantLayout>} />
          <Route path="/t/:tenant/topics/:id" element={<TenantLayout><Topic /></TenantLayout>} />
          <Route path="/t/:tenant/items/:id" element={<TenantLayout><Item /></TenantLayout>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </KbClientContext.Provider>
    </QueryClientProvider>
  );
}

function Gate() {
  const { status } = useAuth();
  if (status === 'loading') {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }
  if (status !== 'signedIn') {
    return (
      <Layout>
        <SignIn />
      </Layout>
    );
  }
  return <SignedInApp />;
}

export function App({ config, keycloakFactory }: { config: AppConfig; keycloakFactory?: KeycloakFactory }) {
  return (
    <AppConfigContext.Provider value={config}>
      <BrowserRouter>
        <AuthProvider config={config} keycloakFactory={keycloakFactory}>
          <Gate />
        </AuthProvider>
      </BrowserRouter>
    </AppConfigContext.Provider>
  );
}
